import { useEffect, useState } from 'react';
import { api, ApiError, errorText } from '../api';
import { Dialog } from '../components/Dialog';
import { fmtDate } from '../lib/format';
import { useToast } from '../lib/toast';
import { ChipRadios } from './ChipRadios';
import { darf, NurLesen, OHNE_RECHT_EINSTELLUNGEN, tarifFehler } from './tarifHinweis';
import type { Overview } from './types';
import './datev.css';

// DATEV-Export (Funktionsliste Punkt 4): Rechnungen und Zahlungen eines Zeitraums als DATEV-Buchungsstapel samt
// Kunden, zum Einlesen beim Büro. Die Angaben des Büros (Berater- und Mandantennummer, Kontenrahmen …) werden beim
// ersten Mal eingetragen und gespeichert. Billtoro: Mitglied mit dem Recht „Einstellungen“, aber ohne „Exporte“: nur die
// Angaben mit „Speichern“ (geöffnet über „DATEV-Angaben“ in der Rechnungsliste), kein Zeitraum, kein Herunterladen.
// Die Felder der Angaben (DatevFelder) nutzt auch das Steuerbüro mit eigenem Zugang (billtoro/mandanten/Mandant.tsx).

type Skr = 'skr03' | 'skr04';
type Debitor = 'plus' | 'gleich';
// Angaben, wie der Server sie liefert (gespeichert oder vorgeschlagen)
export interface DatevWerte {
  beraternummer: number | null;
  mandantennummer: number | null;
  kontenrahmen: Skr;
  sachkontenlaenge: number;
  bankkonto: string;
  wjBeginn: number;
  debitor: Debitor;
}
interface Gespeichert extends DatevWerte {
  gespeichert: boolean;
  letzter: { von: string; bis: string; am: string } | null;
  // Billtoro: Ausgaben kommen mit in den Stapel (erst, wenn der Server sie dazunimmt)
  ausgaben?: boolean;
}
export interface Angaben {
  beraternummer: string;
  mandantennummer: string;
  kontenrahmen: Skr;
  sachkontenlaenge: number;
  bankkonto: string;
  wjBeginn: number;
  debitor: Debitor;
}
type Range = { key: string; label: string; from: string; to: string };

const BANK: Record<Skr, string> = { skr03: '1200', skr04: '1800' };
const MONATE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

const iso = (y: number, m: number, d: number) => new Date(Date.UTC(y, m - 1, d)).toISOString().slice(0, 10);
const lastDay = (y: number, m: number) => iso(y, m + 1, 0);

function ranges(today: string): Range[] {
  const y = Number(today.slice(0, 4));
  const m = Number(today.slice(5, 7));
  const pm = m === 1 ? 12 : m - 1;
  const py = m === 1 ? y - 1 : y;
  const q = Math.floor((m - 1) / 3) * 3 + 1;
  const pq = q === 1 ? 10 : q - 3;
  const pqy = q === 1 ? y - 1 : y;
  return [
    { key: 'vormonat', label: 'Letzter Monat', from: iso(py, pm, 1), to: lastDay(py, pm) },
    { key: 'monat', label: 'Dieser Monat', from: iso(y, m, 1), to: lastDay(y, m) },
    { key: 'vorquartal', label: 'Letztes Quartal', from: iso(pqy, pq, 1), to: lastDay(pqy, pq + 2) },
    { key: 'quartal', label: 'Dieses Quartal', from: iso(y, q, 1), to: lastDay(y, q + 2) },
    { key: 'jahr', label: `Jahr ${y}`, from: iso(y, 1, 1), to: iso(y, 12, 31) },
    { key: 'vorjahr', label: `Jahr ${y - 1}`, from: iso(y - 1, 1, 1), to: iso(y - 1, 12, 31) },
  ];
}

// Wie der Server: ein Stapel liegt in einem Wirtschaftsjahr und in einem Kalenderjahr
function zeitraumFehler(from: string, to: string, wjBeginn: number): string | null {
  if (!from || !to) return 'Bitte den Zeitraum wählen.';
  if (from > to) return 'Der Anfang liegt nach dem Ende.';
  const y = Number(from.slice(0, 4));
  const jahr = Number(from.slice(5, 7)) >= wjBeginn ? y : y - 1;
  const beginn = iso(jahr, wjBeginn, 1);
  const ende = iso(jahr + 1, wjBeginn, 0);
  if (to > ende) return `Der Zeitraum geht über das Wirtschaftsjahr (${fmtDate(beginn)} bis ${fmtDate(ende)}) hinaus.`;
  if (from.slice(0, 4) !== to.slice(0, 4)) return 'Bitte einen Zeitraum innerhalb eines Kalenderjahres wählen.';
  return null;
}

const anzahl = (n: number, eins: string, viele: string) => (n === 0 ? `keine ${viele}` : n === 1 ? `1 ${eins}` : `${n} ${viele}`);
const ziffern = (s: string) => (/^\d+$/.test(s.trim()) ? Number(s.trim()) : null);
export const angabenVon = (g: DatevWerte): Angaben => ({
  beraternummer: g.beraternummer ? String(g.beraternummer) : '',
  mandantennummer: g.mandantennummer ? String(g.mandantennummer) : '',
  kontenrahmen: g.kontenrahmen,
  sachkontenlaenge: g.sachkontenlaenge,
  bankkonto: g.bankkonto,
  wjBeginn: g.wjBeginn,
  debitor: g.debitor,
});
// Inhalt für PUT …/datev/einstellungen (Firma) und PUT /api/billtoro/mandanten/:id/datev (Steuerbüro)
export const angabenDaten = (a: Angaben) => ({
  beraternummer: ziffern(a.beraternummer),
  mandantennummer: ziffern(a.mandantennummer),
  kontenrahmen: a.kontenrahmen,
  sachkontenlaenge: a.sachkontenlaenge,
  bankkonto: a.bankkonto.trim(),
  wjBeginn: a.wjBeginn,
  debitor: a.debitor,
});

function speichern(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

export function DatevDialog({ data, open, onClose }: { data: Overview; open: boolean; onClose(): void }) {
  const toast = useToast();
  const all = ranges(data.today);
  const [key, setKey] = useState(all[0]!.key);
  const [from, setFrom] = useState(all[0]!.from);
  const [to, setTo] = useState(all[0]!.to);
  const [belege, setBelege] = useState(false);
  const [stand, setStand] = useState<Gespeichert | null>(null);
  const [f, setF] = useState<Angaben | null>(null);
  const [offen, setOffen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const base = `/api/rechnungstool/${data.firm.id}/datev`;
  // Billtoro: Zeitraum und Herunterladen nur mit dem Recht „Exporte“
  const exportieren = darf(data.tarif, 'export');

  useEffect(() => {
    if (!open) return;
    let aktiv = true;
    setError(null);
    api
      .get<Gespeichert>(`${base}/einstellungen`)
      .then((g) => {
        if (!aktiv) return;
        setStand(g);
        setF(angabenVon(g));
        // beim ersten Mal aufgeklappt, ohne Export immer
        setOffen(!g.gespeichert || !exportieren);
      })
      .catch((err) => {
        if (!aktiv) return;
        // Tarif-Fehler (Billtoro): das Fenster „Mehr mit Billtoro“ übernimmt, dieses geht zu
        if (tarifFehler(err)) onClose();
        else setError(errorText(err));
      });
    return () => {
      aktiv = false;
    };
    // onClose ändert sich mit jeder Darstellung, gilt aber gleich
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, base]);

  const pick = (r: Range) => {
    setKey(r.key);
    setFrom(r.from);
    setTo(r.to);
    setError(null);
  };
  const aendern = (neu: Angaben) => {
    setF(neu);
    setError(null);
  };

  const problem = f ? zeitraumFehler(from, to, f.wjBeginn) : null;
  const im = (d: string) => d >= from && d <= to;
  const rechnungen = data.items.filter((i) => im(i.invoiceDate)).length;
  // Rechnung oder Zahlung in anderer Währung im Zeitraum: der Hinweis zu Kursunterschieden
  const fremd = data.items.some((i) => i.waehrung !== 'EUR' && (im(i.invoiceDate) || i.zahlungen.some((z) => im(z.datum))));
  const zahlungen = data.items.reduce((s, i) => s + i.zahlungen.filter((z) => im(z.datum)).length, 0);
  const geaendert = !!stand && !!f && (!stand.gespeichert || JSON.stringify(f) !== JSON.stringify(angabenVon(stand)));
  const letzter = stand?.letzter;
  const doppelt = !!letzter && from <= letzter.bis && to >= letzter.von;

  const lesen = !darf(data.tarif, 'einstellungen');

  const angabenSpeichern = (a: Angaben) => api.put(`${base}/einstellungen`, angabenDaten(a));

  // ohne das Recht „Exporte“: nur die Angaben speichern
  async function speichernOhneExport() {
    if (!f) return;
    setBusy(true);
    setError(null);
    try {
      await angabenSpeichern(f);
      toast('DATEV-Angaben gespeichert');
      onClose();
    } catch (err) {
      if (tarifFehler(err)) return onClose();
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function laden() {
    if (!f || problem) return;
    setBusy(true);
    setError(null);
    try {
      // Billtoro: ohne das Recht „Einstellungen“ mit den gespeicherten Angaben (ändern nur mit dem Recht)
      if (geaendert && !lesen) await angabenSpeichern(f);
      const q = new URLSearchParams({ from, to });
      if (belege) q.set('belege', '1');
      const blob = await api.file(`${base}?${q}`);
      const monat = from.endsWith('-01') && to === lastDay(Number(from.slice(0, 4)), Number(from.slice(5, 7)));
      const firma = data.firm.name.replace(/[\\/:*?"<>|]+/g, ' ').trim();
      speichern(blob, `DATEV ${firma} ${monat ? from.slice(0, 7) : `${from}_${to}`}.zip`);
      toast('DATEV-Export heruntergeladen');
      const g = await api.get<Gespeichert>(`${base}/einstellungen`);
      setStand(g);
      setF(angabenVon(g));
      setOffen(false);
    } catch (err) {
      if (tarifFehler(err)) return onClose();
      setError(errorText(err));
      // Angaben fehlen oder stimmen nicht: aufklappen, damit man sie sieht
      if (err instanceof ApiError && ['invalid', 'datev_angaben', 'kundennummer'].includes(err.code ?? '')) setOffen(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} title={exportieren ? 'DATEV-Export' : 'DATEV-Angaben'} className="rt-exp-dlg dv-dlg">
      <p className="muted dv-lead">
        {exportieren
          ? 'Rechnungen und Zahlungen als Buchungsstapel für Ihr Büro oder Steuerbüro, dazu Ihre Kunden.'
          : 'Die Angaben fürs Büro für den DATEV-Export. Herunterladen darf, wem der Inhaber das Recht „Exporte“ gibt.'}
      </p>
      {!f ? (
        error ? null : <p className="muted">Lädt …</p>
      ) : (
        <>
          {exportieren && (
            <>
              <div className="rt-chips dv-ranges" role="group" aria-label="Zeitraum">
                {all.map((r) => (
                  <button key={r.key} type="button" className={`rt-chip${key === r.key ? ' on' : ''}`} aria-pressed={key === r.key} onClick={() => pick(r)}>
                    {r.label}
                  </button>
                ))}
              </div>
              <div className="rt-grid rt-grid2">
                <label className="field">
                  <span>Von</span>
                  <input
                    type="date"
                    value={from}
                    onChange={(e) => {
                      setKey('');
                      setFrom(e.target.value);
                    }}
                  />
                </label>
                <label className="field">
                  <span>Bis</span>
                  <input
                    type="date"
                    value={to}
                    onChange={(e) => {
                      setKey('');
                      setTo(e.target.value);
                    }}
                  />
                </label>
              </div>
              <p className={`dv-count${problem ? ' bad' : ''}`}>
                {problem ??
                  (rechnungen + zahlungen === 0
                    ? stand?.ausgaben
                      ? 'Keine Rechnungen und keine Zahlungen im Zeitraum. Ausgaben kommen mit, wenn es welche gibt.'
                      : 'Im Zeitraum gibt es keine Rechnungen und keine Zahlungen.'
                    : `${anzahl(rechnungen, 'Rechnung', 'Rechnungen')} und ${anzahl(zahlungen, 'Zahlung', 'Zahlungen')} im Zeitraum`)}
              </p>
              {fremd && !problem && (
                <p className="dv-fremd">Rechnungen in Fremdwährung sind zum Kurs der Rechnung umgerechnet. Kursunterschiede bitte im Steuerbüro buchen.</p>
              )}
              {letzter &&
                (doppelt ? (
                  <p className="dv-last warn">
                    Dieser Zeitraum war schon im Export vom {fmtDate(letzter.am)} ({fmtDate(letzter.von)} bis {fmtDate(letzter.bis)}). Zweimal
                    eingelesen, stehen die Buchungen doppelt da.
                  </p>
                ) : (
                  <p className="dv-last">
                    Zuletzt exportiert am {fmtDate(letzter.am)}: {fmtDate(letzter.von)} bis {fmtDate(letzter.bis)}.
                  </p>
                ))}
            </>
          )}

          <details className="dv-angaben" open={offen} onToggle={(e) => setOffen((e.target as HTMLDetailsElement).open)}>
            <summary>
              <b>Angaben fürs Büro</b>
              {stand?.gespeichert && !offen && (
                <span>
                  Berater {stand.beraternummer} · Mandant {stand.mandantennummer} · {stand.kontenrahmen.toUpperCase()}
                </span>
              )}
            </summary>
            <p className="rt-hint">Diese Angaben bekommen Sie von Ihrem Büro oder Steuerbüro. Sie bleiben gespeichert.</p>
            <NurLesen an={lesen} hinweis={OHNE_RECHT_EINSTELLUNGEN}>
              <DatevFelder f={f} onWert={aendern} />
            </NurLesen>
          </details>

          {exportieren && (
            <label className="check">
              <input type="checkbox" checked={belege} onChange={(e) => setBelege(e.target.checked)} />
              <span>Rechnungen als PDF beilegen</span>
            </label>
          )}
        </>
      )}
      {error && <div className="notice bad">{error}</div>}
      <div className="dlg-actions">
        <button type="button" className="btn" onClick={onClose}>
          Schließen
        </button>
        {exportieren ? (
          <button type="button" className="btn primary" disabled={!f || !!problem || busy} onClick={() => void laden()}>
            {busy ? 'Wird erstellt …' : 'Herunterladen'}
          </button>
        ) : (
          !lesen && (
            <button type="button" className="btn primary" disabled={!f || !geaendert || busy} onClick={() => void speichernOhneExport()}>
              {busy ? 'Wird gespeichert …' : 'Speichern'}
            </button>
          )
        )}
      </div>
    </Dialog>
  );
}

// Die Angaben fürs Büro (ohne Zeitraum): Kontenrahmen gewechselt, dann auch das Bankkonto, solange es das übliche ist
export function DatevFelder({ f, onWert }: { f: Angaben; onWert(f: Angaben): void }) {
  const set = <K extends keyof Angaben>(k: K, v: Angaben[K]) => onWert({ ...f, [k]: v });
  const skr = (v: Skr) => onWert({ ...f, kontenrahmen: v, bankkonto: f.bankkonto === BANK[f.kontenrahmen] ? BANK[v] : f.bankkonto });
  return (
    <>
      <div className="rt-grid rt-grid2">
        <label className="field">
          <span>Beraternummer</span>
          <input value={f.beraternummer} inputMode="numeric" maxLength={7} onChange={(e) => set('beraternummer', e.target.value)} />
        </label>
        <label className="field">
          <span>Mandantennummer</span>
          <input value={f.mandantennummer} inputMode="numeric" maxLength={5} onChange={(e) => set('mandantennummer', e.target.value)} />
        </label>
      </div>
      <div className="field">
        <span>Kontenrahmen</span>
        <ChipRadios
          label="Kontenrahmen"
          value={f.kontenrahmen}
          options={[
            ['skr03', 'SKR03'],
            ['skr04', 'SKR04'],
          ]}
          onChange={skr}
        />
      </div>
      <div className="rt-grid rt-grid2">
        <label className="field">
          <span>Bankkonto</span>
          <input value={f.bankkonto} inputMode="numeric" maxLength={8} onChange={(e) => set('bankkonto', e.target.value)} />
        </label>
        <label className="field">
          <span>Sachkontenlänge</span>
          <select value={f.sachkontenlaenge} onChange={(e) => set('sachkontenlaenge', Number(e.target.value))}>
            {[4, 5, 6, 7, 8].map((n) => (
              <option key={n} value={n}>
                {n} Stellen
              </option>
            ))}
          </select>
        </label>
      </div>
      <label className="field">
        <span>Wirtschaftsjahr beginnt im</span>
        <select value={f.wjBeginn} onChange={(e) => set('wjBeginn', Number(e.target.value))}>
          {MONATE.map((m, i) => (
            <option key={m} value={i + 1}>
              {i === 0 ? 'Januar (wie das Kalenderjahr)' : m}
            </option>
          ))}
        </select>
      </label>
      <div className="field">
        <span>Debitor</span>
        <ChipRadios
          label="Debitor"
          value={f.debitor}
          options={[
            ['plus', `${10 ** f.sachkontenlaenge} + Kundennummer`],
            ['gleich', 'Kundennummer = Debitor'],
          ]}
          onChange={(v) => set('debitor', v)}
        />
        <small>„Kundennummer = Debitor“, wenn Ihre Kundennummern schon die Debitoren beim Büro sind (ab 10000).</small>
      </div>
    </>
  );
}
